import React, { useEffect, useState } from 'react';
import { message, Modal, Form, Input, InputNumber, Select, Switch } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { warningAPI, WarnRule } from '../../services/api';

// /warning/rules — 阈值规则配置 (WarningRule CRUD)
const TH = {
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  ok: '#1E8449', warn: '#D4770C', critical: '#C0392B',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};
const PARAM_LABEL: Record<string, string> = {
  pressure: '施工压力', pump_rate: '排量', proppant_conc: '砂比', casing_pressure: '套压',
};
const LEVEL_LABEL: Record<string, string> = { info: '提示', warning: '警告', critical: '严重' };
const LEVEL_COLOR: Record<string, string> = { info: '#2874A6', warning: TH.warn, critical: TH.critical };

export default function WarningRulesPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const [rules, setRules] = useState<WarnRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<WarnRule | 'new' | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await warningAPI.getRules(projectId);
      setRules(Array.isArray(data) ? data : (data as any)?.results ?? []);
    } catch (e: any) { message.error(e?.message || '加载失败'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const openEdit = (r: WarnRule | 'new') => {
    setEdit(r); form.resetFields();
    form.setFieldsValue(r === 'new'
      ? { parameter: 'pressure', level: 'warning', is_active: true }
      : r);
  };

  const submit = async () => {
    const v = await form.validateFields();
    if (!projectId) return;
    try {
      if (edit === 'new') { await warningAPI.createRule({ ...v, project: projectId }); message.success('已新增'); }
      else if (edit) { await warningAPI.updateRule(edit.id, v); message.success('已更新'); }
      setEdit(null); form.resetFields(); await load();
    } catch (e: any) {
      const d = e?.response?.data;
      message.error(typeof d === 'string' ? d : d?.detail || '保存失败');
    }
  };

  const remove = (r: WarnRule) => Modal.confirm({
    title: `删除规则 "${r.name}"?`,
    onOk: async () => { try { await warningAPI.deleteRule(r.id); message.success('已删除'); await load(); } catch { message.error('删除失败'); } },
  });

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#fff', fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>预警规则配置</h2>
          <span style={{ fontSize: 12, color: TH.textMuted }}>{loading ? '加载中…' : `${rules.length} 条规则`}</span>
        </div>
        <button onClick={() => openEdit('new')} style={{
          padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
          background: TH.accent, color: '#fff', border: 'none', cursor: 'pointer',
        }}>+ 新增规则</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '8px 20px' }}>
        <div style={{ fontSize: 12, color: TH.textMuted, marginBottom: 8 }}>
          阈值规则用于实时监测时的简单越限报警(施工压力/排量/砂比/套压超出区间即触发);复杂工况诊断走 Nolte-Smith 双对数分析。
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: TH.panelHeaderBg, position: 'sticky', top: 0 }}>
              {['规则名', '监测参数', '下限', '上限', '等级', '启用', '建议措施', '操作'].map((h) => (
                <th key={h} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: TH.textMuted }}>加载中…</td></tr>
            ) : rules.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: TH.textMuted }}>无规则 — 点右上「+ 新增规则」</td></tr>
            ) : rules.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid #F0EDE4', opacity: r.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '5px 10px', fontWeight: 600 }}>{r.name}</td>
                <td style={{ padding: '5px 10px' }}>{PARAM_LABEL[r.parameter] || r.parameter}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{r.threshold_min ?? '—'}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{r.threshold_max ?? '—'}</td>
                <td style={{ padding: '5px 10px' }}>
                  <span style={{ padding: '2px 8px', fontSize: 11, borderRadius: 10, background: LEVEL_COLOR[r.level], color: '#fff' }}>{LEVEL_LABEL[r.level] || r.level}</span>
                </td>
                <td style={{ padding: '5px 10px' }}>{r.is_active ? <span style={{ color: TH.ok }}>✓</span> : <span style={{ color: TH.textMuted }}>停用</span>}</td>
                <td style={{ padding: '5px 10px', color: TH.textSec, maxWidth: 260 }}>{r.action_suggestion || '—'}</td>
                <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                  <button onClick={() => openEdit(r)} style={btnSm}>编辑</button>
                  <button onClick={() => remove(r)} style={{ ...btnSm, marginLeft: 4, color: TH.warn }}>删</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={edit !== null} title={edit === 'new' ? '新增预警规则' : `编辑 ${(edit as WarnRule)?.name}`}
        onCancel={() => { setEdit(null); form.resetFields(); }} onOk={submit} okText="保存" cancelText="取消" destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="name" label="规则名" rules={[{ required: true }]}><Input placeholder="例: 施工压力超限" /></Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Form.Item name="parameter" label="监测参数" rules={[{ required: true }]}>
              <Select options={Object.entries(PARAM_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
            </Form.Item>
            <Form.Item name="level" label="预警等级">
              <Select options={Object.entries(LEVEL_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
            </Form.Item>
            <Form.Item name="threshold_min" label="下限阈值"><InputNumber style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="threshold_max" label="上限阈值"><InputNumber style={{ width: '100%' }} /></Form.Item>
          </div>
          <Form.Item name="action_suggestion" label="建议措施"><Input.TextArea rows={2} placeholder="触发后的处置建议" /></Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

const btnSm: React.CSSProperties = {
  padding: '2px 8px', fontSize: 11, border: `1px solid ${TH.headerBorder}`,
  background: '#fff', borderRadius: 3, cursor: 'pointer', color: TH.textSec,
};
